import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navLinks } from '@/mocks/home';
import BookButton from '@/pages/home/components/BookButton';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // On home, stay transparent until the pinned Hero has fully scrolled past
    const onScroll = () => {
      const hero = window.location.pathname.endsWith('/') || window.location.pathname === '' ? document.getElementById('top') : null;
      setScrolled(hero ? hero.getBoundingClientRect().bottom <= 80 : window.scrollY > 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const onNavClick = (href: string) => {
    setOpen(false);
    if (href === location.pathname && !location.hash) window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Route scroll is owned by RouteTransition (layout phase); only close the menu here
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const linkCls = ({ isActive }: { isActive: boolean }) => `text-[13px] tracking-wide whitespace-nowrap cursor-pointer transition-colors pb-1 border-b ${isActive ? 'text-foreground-950 border-foreground-950' : 'text-foreground-700 border-transparent hover:text-foreground-950'}`;
  const solid = scrolled || open || location.pathname !== '/';

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-transparent'}`}>
      <nav className="flex items-center justify-between h-16 md:h-20 px-4 md:px-12 lg:px-16">
        <Link to="/" className="font-heading font-bold text-2xl md:text-[26px] tracking-[-0.04em] text-foreground-950 cursor-pointer">NOVA</Link>
        <ul className="hidden md:flex items-center gap-7 lg:gap-10">
          {navLinks.map((l) => (
            <li key={l.label}><NavLink to={l.href} end onClick={() => onNavClick(l.href)} className={linkCls}>{l.label}</NavLink></li>
          ))}
        </ul>
        <div className="hidden md:block"><BookButton small /></div>
        <button type="button" aria-label="Menu" onClick={() => setOpen(!open)} aria-expanded={open} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap">
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </nav>
      {open && (
        <div className="md:hidden px-4 pb-6 flex flex-col gap-4 bg-background-50">
          {navLinks.map((l) => (
            <NavLink key={l.label} to={l.href} end onClick={() => onNavClick(l.href)} className={({ isActive }) => `text-sm py-1 cursor-pointer ${isActive ? 'text-foreground-950 font-semibold' : 'text-foreground-800'}`}>{l.label}</NavLink>
          ))}
          <div onClick={() => setOpen(false)}><BookButton /></div>
        </div>
      )}
    </header>
  );
}